<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-people me-2"></i>Users Management
      </h4>
      <div class="d-flex">
        <button
          v-if="profile.role !== 'user'"
          class="btn btn-primary"
          @click="userModal = true; userMode = 'add'"
        >
          <i class="bi bi-plus-circle me-1"></i> Add User
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading users...</p>
      </div>

      <!-- Users Content -->
      <div v-if="!loading" class="users-content fade-in">
        <!-- Users Count & Search -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ users.total }}</span>
            <span class="text-muted">Users</span>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <UInput v-model="dataTableSearch" size="md" placeholder="Search user" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <!-- Users Table -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>NAME</th>
                  <th>PHONE</th>
                  <th>EMAIL</th>
                  <th>ROLE</th>
                  <th>STATUS</th>
                  <th>CREATED</th>
                  <th class="text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(user, index) in users.data" :key="user.id" class="user-row" :class="{'bg-light': index % 2 === 0}">
                  <td>
                    <div class="d-flex align-items-center">
                      <i class="bi bi-person-circle text-primary me-2"></i>
                      <strong>{{ name(user) }}</strong>
                    </div>
                  </td>
                  <td>{{ user.phone }}</td>
                  <td>
                    <span class="text-muted">{{ user.email }}</span>
                  </td>
                  <td>
                    <span class="badge bg-secondary">{{ user.role }}</span>
                  </td>
                  <td>
                    <Badge pill :type="getPillByStatus(user.status)">{{ user.status }}</Badge>
                  </td>
                  <td>
                    <i class="bi bi-calendar me-1 text-muted"></i>
                    {{ formatDate(user.created_at) }}
                  </td>
                  <td class="text-end">
                    <div class="d-flex gap-2 justify-content-end">
                      <button
                        v-if="profile.role !== 'user'"
                        class="btn btn-sm btn-outline-primary"
                        @click="userModal = true; userMode = 'edit'; populateUserEdit(user)"
                      >
                        <i class="bi bi-pencil me-1"></i> Edit
                      </button>
                      <button
                        v-if="profile.role === 'super-admin' || profile.role === 'primary'"
                        class="btn btn-sm btn-outline-info"
                        @click="openPermissionsModal(user)"
                      >
                        <i class="bi bi-shield-lock me-1"></i> Permissions
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <!-- Back Button -->
        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>

    <!-- Edit User Modal -->
    <Modal :open="userModal" @closed="userModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0" v-if="userMode === 'edit'">
          <i class="bi bi-pencil-square me-2 text-primary"></i>Edit User
        </h5>
        <h5 class="modal-title m-0" v-else>
          <i class="bi bi-person-plus me-2 text-primary"></i>Add User
        </h5>
        <button @click="userModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row g-3">
            <div class="col-md-6">
              <label for="first_name" class="form-label fw-bold">First Name</label>
              <div class="input-group">
                <span class="input-group-text bg-white">
                  <i class="bi bi-person text-primary"></i>
                </span>
                <input
                  type="text"
                  class="form-control border-start-0"
                  placeholder="Enter first name"
                  v-model="user.first_name"
                >
              </div>
            </div>
            <div class="col-md-6">
              <label for="last_name" class="form-label fw-bold">Last Name</label>
              <div class="input-group">
                <span class="input-group-text bg-white">
                  <i class="bi bi-person text-primary"></i>
                </span>
                <input
                  type="text"
                  class="form-control border-start-0"
                  placeholder="Enter last name"
                  v-model="user.last_name"
                >
              </div>
            </div>
            <div class="col-md-6">
              <label for="email" class="form-label fw-bold">Email</label>
              <div class="input-group">
                <span class="input-group-text bg-white">
                  <i class="bi bi-envelope text-primary"></i>
                </span>
                <input
                  type="email"
                  class="form-control border-start-0"
                  placeholder="Enter email address"
                  v-model="user.email"
                >
              </div>
            </div>
            <div class="col-md-6">
              <label for="phone" class="form-label fw-bold">Phone</label>
              <div class="input-group">
                <span class="input-group-text bg-white">
                  <i class="bi bi-telephone text-primary"></i>
                </span>
                <input
                  type="text"
                  class="form-control border-start-0"
                  placeholder="Enter phone number"
                  v-model="user.phone"
                >
              </div>
            </div>
            <div class="col-md-6">
              <label for="role" class="form-label fw-bold">Role</label>
              <div class="input-group">
                <span class="input-group-text bg-white">
                  <i class="bi bi-person-badge text-primary"></i>
                </span>
                <select class="form-select border-start-0" v-model="user.role">
                  <option value="" disabled selected>Select role</option>
                  <option value="primary">Primary</option>
                  <option value="manager">Branch Manager</option>
                  <option value="admin">Admin</option>
                  <option value="inventory">Inventory</option>
                  <option value="cashier">Cashier</option>
                  <option value="user">User</option>
                </select>
              </div>
            </div>
            <div class="col-md-6">
              <label for="status" class="form-label fw-bold">Status</label>
              <div class="input-group">
                <span class="input-group-text bg-white">
                  <i class="bi bi-toggle-on text-primary"></i>
                </span>
                <select class="form-select border-start-0" v-model="user.status">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
        </form>

        <div v-if="userMode === 'add' || userMode === 'edit'" class="mt-4">
          <div class="card bg-light">
            <div class="card-header">
              <h5 class="m-0">
                <i class="bi bi-info-circle me-2 text-primary"></i>User Roles Explained
              </h5>
            </div>
            <div class="card-body p-0">
              <div class="accordion">
                <div class="accordion-item border-0 rounded-0" id="primary" @click="accordionToggle('primary')">
                  <div class="accordion-header p-3 d-flex align-items-center">
                    <i class="bi bi-star-fill text-warning me-2"></i>
                    <strong>Primary</strong>
                  </div>
                  <div class="collapse-holder bg-white d-none">
                    <div class="accordion-body">
                      <ul class="mb-0 ps-3">
                        <li>Organization owner with full access.</li>
                        <li>Can access all modules and pages.</li>
                        <li>Can create, update, and remove data in all modules.</li>
                        <li>Can manage organization settings and users.</li>
                        <li>Full access to analytics and reports.</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="accordion-item border-0 rounded-0" id="branch_manager" @click="accordionToggle('branch_manager')">
                  <div class="accordion-header p-3 d-flex align-items-center">
                    <i class="bi bi-building text-info me-2"></i>
                    <strong>Branch Manager</strong>
                  </div>
                  <div class="collapse-holder bg-white d-none">
                    <div class="accordion-body">
                      <ul class="mb-0 ps-3">
                        <li>Manages specific store/branch operations.</li>
                        <li>Full access to inventory, procurement, and sales.</li>
                        <li>Can approve transfers and returns.</li>
                        <li>Access to store-level analytics and reports.</li>
                        <li>Can manage store users.</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="accordion-item border-0 rounded-0" id="admin" @click="accordionToggle('admin')">
                  <div class="accordion-header p-3 d-flex align-items-center">
                    <i class="bi bi-shield text-primary me-2"></i>
                    <strong>Admin</strong>
                  </div>
                  <div class="collapse-holder bg-white d-none">
                    <div class="accordion-body">
                      <ul class="mb-0 ps-3">
                        <li>Administrative access to most modules.</li>
                        <li>Can manage inventory, procurement, and sales.</li>
                        <li>Access to reports and analytics.</li>
                        <li>Cannot manage users or system settings.</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="accordion-item border-0 rounded-0" id="inventory" @click="accordionToggle('inventory')">
                  <div class="accordion-header p-3 d-flex align-items-center">
                    <i class="bi bi-box-seam text-success me-2"></i>
                    <strong>Inventory</strong>
                  </div>
                  <div class="collapse-holder bg-white d-none">
                    <div class="accordion-body">
                      <ul class="mb-0 ps-3">
                        <li>Focused on inventory management.</li>
                        <li>Can view and manage stock levels.</li>
                        <li>Can process stock takes.</li>
                        <li>Limited access to POS and sales.</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="accordion-item border-0 rounded-0" id="cashier" @click="accordionToggle('cashier')">
                  <div class="accordion-header p-3 d-flex align-items-center">
                    <i class="bi bi-cash-coin text-warning me-2"></i>
                    <strong>Cashier</strong>
                  </div>
                  <div class="collapse-holder bg-white d-none">
                    <div class="accordion-body">
                      <ul class="mb-0 ps-3">
                        <li>Primary focus on POS operations.</li>
                        <li>Can process sales and returns.</li>
                        <li>Can view products and stock levels.</li>
                        <li>Can manage customers.</li>
                        <li>Limited access to reports.</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="accordion-item border-0 rounded-0" id="user" @click="accordionToggle('user')">
                  <div class="accordion-header p-3 d-flex align-items-center">
                    <i class="bi bi-person text-secondary me-2"></i>
                    <strong>User</strong>
                  </div>
                  <div class="collapse-holder bg-white d-none">
                    <div class="accordion-body">
                      <ul class="mb-0 ps-3">
                        <li>Basic POS access for sales.</li>
                        <li>Can view products and customers.</li>
                        <li>Limited to essential sales operations.</li>
                        <li>No access to management features.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="userModal = false">
          <i class="bi bi-x-circle me-1"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" v-if="userMode === 'add'" @click="addUser">
          <i class="bi bi-plus-circle me-1"></i> Add User
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updateUser(user.id)">
          <i class="bi bi-check-circle me-1"></i> Update User
        </button>
      </div>
    </Modal>
    <!-- Edit User Modal End -->

    <!-- Permissions Modal -->
    <Modal :open="permissionsModal" @closed="permissionsModal = false" dialog-size="modal-xl">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi bi-shield-lock me-2 text-info"></i>Manage Permissions - {{ selectedUser ? name(selectedUser) : '' }}
        </h5>
        <button @click="permissionsModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <div v-if="loadingPermissions" class="text-center py-5">
          <Spinner spinner-class="mb-1" />
          <p class="text-muted mt-2">Loading permissions...</p>
        </div>

        <div v-else>
          <!-- Search Permissions -->
          <div class="mb-4">
            <div class="input-group">
              <span class="input-group-text bg-light">
                <i class="bi bi-search"></i>
              </span>
              <input 
                v-model="permissionSearch" 
                type="text" 
                class="form-control" 
                placeholder="Search permissions by name or display name..."
              />
              <button 
                v-if="permissionSearch" 
                class="btn btn-outline-secondary" 
                type="button"
                @click="permissionSearch = ''"
              >
                <i class="bi bi-x-circle"></i> Clear
              </button>
            </div>
            <div v-if="permissionSearch && permissionSearch.trim()" class="mt-2">
              <small class="text-muted">
                <i class="bi bi-info-circle me-1"></i>
                Found {{ totalFilteredPermissions }} permission(s) in {{ Object.keys(filteredPermissions).length }} category(ies)
              </small>
            </div>
            <div v-if="permissionSearch && permissionSearch.trim() && totalFilteredPermissions === 0" class="mt-2">
              <small class="text-warning">
                <i class="bi bi-exclamation-triangle me-1"></i>
                No permissions found matching "{{ permissionSearch }}"
              </small>
            </div>
          </div>

          <!-- User's Current Permissions Summary -->
          <div class="card bg-light mb-4">
            <div class="card-header">
              <h6 class="m-0">
                <i class="bi bi-info-circle me-2"></i>Current Permissions Summary
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <p class="mb-2"><strong>Role:</strong> <span class="badge bg-secondary">{{ selectedUser?.role }}</span></p>
                  <p class="mb-2"><strong>Total Permissions:</strong> {{ userPermissions.all_permissions?.length || 0 }}</p>
                </div>
                <div class="col-md-6">
                  <p class="mb-2"><strong>Direct Permissions:</strong> {{ userPermissions.direct_permissions?.length || 0 }}</p>
                  <p class="mb-2"><strong>Role Permissions:</strong> {{ userPermissions.role_permissions?.length || 0 }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Permissions by Category -->
          <div v-if="Object.keys(filteredPermissions).length === 0" class="text-center py-5">
            <i class="bi bi-search text-muted" style="font-size: 3rem;"></i>
            <p class="text-muted mt-3">No permissions found matching your search.</p>
            <button class="btn btn-sm btn-outline-primary" @click="permissionSearch = ''">
              <i class="bi bi-arrow-counterclockwise me-1"></i> Clear Search
            </button>
          </div>
          <div v-else class="accordion" id="permissionsAccordion">
            <div v-for="(permissions, category) in filteredPermissions" :key="category" class="accordion-item">
              <h2 class="accordion-header" :id="'heading' + getCategoryId(category)">
                <button 
                  class="accordion-button collapsed" 
                  type="button" 
                  @click="toggleCategory(category)"
                >
                  <i class="bi bi-folder me-2"></i>
                  <strong>{{ category }}</strong>
                  <span class="badge bg-primary ms-2">{{ permissions.length }}</span>
                  <span class="badge bg-success ms-2">{{ getGrantedCount(permissions) }} granted</span>
                </button>
              </h2>
              <div 
                :id="'collapse' + getCategoryId(category)" 
                class="accordion-collapse collapse"
                :class="{ 'show': expandedCategories.includes(category) }"
              >
                <div class="accordion-body">
                  <div class="row">
                    <div v-for="permission in permissions" :key="permission.id" class="col-md-6 col-lg-4 mb-3">
                      <div class="form-check">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          :id="'perm-' + permission.id"
                          :checked="hasPermission(permission.name)"
                          @change="togglePermission(permission.name, $event.target.checked)"
                        >
                        <label class="form-check-label" :for="'perm-' + permission.id">
                          <strong>{{ permission.display_name }}</strong>
                          <br>
                          <small class="text-muted">{{ permission.name }}</small>
                          <span v-if="isRolePermission(permission.name)" class="badge bg-info ms-2" style="font-size: 0.65rem;">From Role</span>
                          <span v-if="isDirectPermission(permission.name)" class="badge bg-warning ms-2" style="font-size: 0.65rem;">Direct</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Changes Summary -->
          <div v-if="permissionChanges.added.length > 0 || permissionChanges.removed.length > 0" class="card bg-warning bg-opacity-10 mt-4">
            <div class="card-header bg-warning bg-opacity-25">
              <h6 class="m-0">
                <i class="bi bi-exclamation-triangle me-2"></i>Pending Changes
              </h6>
            </div>
            <div class="card-body">
              <div v-if="permissionChanges.added.length > 0" class="mb-3">
                <strong class="text-success">To be Added ({{ permissionChanges.added.length }}):</strong>
                <div class="mt-2">
                  <span v-for="perm in permissionChanges.added" :key="perm" class="badge bg-success me-1 mb-1">{{ perm }}</span>
                </div>
              </div>
              <div v-if="permissionChanges.removed.length > 0">
                <strong class="text-danger">To be Removed ({{ permissionChanges.removed.length }}):</strong>
                <div class="mt-2">
                  <span v-for="perm in permissionChanges.removed" :key="perm" class="badge bg-danger me-1 mb-1">{{ perm }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" @click="permissionsModal = false">
          <i class="bi bi-x-circle me-1"></i> Cancel
        </button>
        <button 
          type="button" 
          class="btn btn-primary" 
          @click="savePermissions"
          :disabled="permissionChanges.added.length === 0 && permissionChanges.removed.length === 0"
        >
          <i class="bi bi-check-circle me-1"></i> Save Changes
        </button>
      </div>
    </Modal>
    <!-- Permissions Modal End -->
  </Card>
</template>

<script>
import userService from "@/api/userService";
import permissionService from "@/api/permissionService";
import formatDate from "../../utils/formatDate";
import {decrypt} from "@/utils/crypto";
import Card from "@/components/card/Card";
import UInput from "@/components/input/Input";
import Pagination from "@/components/pagination/Pagination";
import Badge from "@/components/badge/Badge";
import Spinner from "@/components/spinner/Spinner";
import Modal from "@/components/modal/Modal";

export default {
  name: "User",

  components: {
    Card,
    UInput,
    Pagination,
    Badge,
    Spinner,
    Modal
  },

  data(){
    return {
      profile: {},
      loading: true,
      users: {
        data: []
      },
      userMode: 'add',
      userModal: false,
      user: {
        organization_id: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: '',
        status: '',
      },
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
      permissionsModal: false,
      loadingPermissions: false,
      selectedUser: null,
      allPermissions: {},
      userPermissions: {
        all_permissions: [],
        direct_permissions: [],
        role_permissions: []
      },
      permissionChanges: {
        added: [],
        removed: []
      },
      expandedCategories: [],
      originalDirectPermissions: [],
      permissionSearch: '',
    }
  },

  computed: {
    filteredPermissions() {
      if (!this.allPermissions || typeof this.allPermissions !== 'object') {
        return {}
      }

      if (!this.permissionSearch || !this.permissionSearch.trim()) {
        return this.allPermissions
      }

      const searchTerm = this.permissionSearch.toLowerCase().trim()
      const filtered = {}

      Object.keys(this.allPermissions).forEach(category => {
        const matchingPermissions = this.allPermissions[category].filter(permission => {
          return permission.name.toLowerCase().includes(searchTerm) ||
                 permission.display_name.toLowerCase().includes(searchTerm) ||
                 category.toLowerCase().includes(searchTerm)
        })

        if (matchingPermissions.length > 0) {
          filtered[category] = matchingPermissions
        }
      })

      return filtered
    },
    totalFilteredPermissions() {
      if (!this.filteredPermissions || typeof this.filteredPermissions !== 'object') {
        return 0
      }
      return Object.values(this.filteredPermissions).reduce((sum, perms) => sum + perms.length, 0)
    },
    totalPages(){
      return this.users?.last_page || 1
    }
  },

  methods: {
    formatDate,
    getUsers: async function () {
      this.loading = true
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch?.toString().trim() || undefined,
      }
      this.users = await userService.getUsers(params);
      this.loading = false
    },
    onSearchClick(){
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getUsers()
    },
    onClearSearchClick(){
      if (!this.dataTableSearch) return;
      this.dataTableSearch = ''
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getUsers()
    },
    updateRouteQuery() {
      const query = {
        ...this.$route.query,
        page: this.dataTablePage !== 1 ? this.dataTablePage : undefined,
        per_page: this.dataTablePerPage !== 10 ? this.dataTablePerPage : undefined,
        search: this.dataTableSearch && this.dataTableSearch.trim().length ? this.dataTableSearch.trim() : undefined,
      }
      Object.keys(query).forEach(k => query[k] === undefined && delete query[k])
      this.$router.replace({ path: this.$route.path, query })
    },
    name (user) {
      return `${user.first_name} ${user.last_name}`
    },
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'inactive':
          return 'danger';
        case 'active':
          return 'success';
      }
    },
    populateUserEdit(user) {
      this.user = {...user}
    },
    async addUser() {
      let response = await userService.createUser(this.user)
      if (response) {
        this.userModal = false
        await this.getUsers()
      }
    },
    async updateUser(id) {
      let response = await userService.updateUser(this.user, id)
      if (response) {
        this.userModal = false
        await this.getUsers()
      }
    },
    accordionToggle: function (id) {
      let ids = ["super_admin", "primary", "branch_manager", "admin", "inventory", "cashier", "user"];
      for (let i = 0; i < ids.length; i++) {
        let accordion = document.getElementById(ids[i]);
        let collapseHolder = accordion.getElementsByClassName("collapse-holder");
        if (ids[i] === id) {
          if (collapseHolder[0].classList.contains("d-none")) {
            collapseHolder[0].classList.remove("d-none");
          } else {
            collapseHolder[0].classList.add("d-none");
          }
        } else {
          collapseHolder[0].classList.add("d-none");
        }
      }
    },
    async openPermissionsModal(user) {
      this.selectedUser = user
      this.permissionsModal = true
      this.loadingPermissions = true
      this.permissionChanges = { added: [], removed: [] }
      this.expandedCategories = []
      
      try {
        const [allPerms, userPerms] = await Promise.all([
          permissionService.getAllPermissions(),
          permissionService.getUserPermissions(user.id)
        ])
        
        this.allPermissions = allPerms
        this.userPermissions = userPerms
        this.originalDirectPermissions = [...(userPerms.direct_permissions || [])]
      } catch (error) {
        console.error('Error loading permissions:', error)
      } finally {
        this.loadingPermissions = false
      }
    },
    getCategoryId(category) {
      return category.replace(/\s+/g, '-').toLowerCase()
    },
    toggleCategory(category) {
      const index = this.expandedCategories.indexOf(category)
      if (index > -1) {
        this.expandedCategories.splice(index, 1)
      } else {
        this.expandedCategories.push(category)
      }
    },
    hasPermission(permissionName) {
      return this.userPermissions.all_permissions?.includes(permissionName) || false
    },
    isRolePermission(permissionName) {
      return this.userPermissions.role_permissions?.includes(permissionName) || false
    },
    isDirectPermission(permissionName) {
      return this.userPermissions.direct_permissions?.includes(permissionName) || false
    },
    getGrantedCount(permissions) {
      return permissions.filter(p => this.hasPermission(p.name)).length
    },
    togglePermission(permissionName, isChecked) {
      const isRolePerm = this.isRolePermission(permissionName)
      const wasDirectPerm = this.originalDirectPermissions.includes(permissionName)
      
      if (isChecked) {
        if (!isRolePerm && !wasDirectPerm) {
          if (!this.permissionChanges.added.includes(permissionName)) {
            this.permissionChanges.added.push(permissionName)
          }
          const removeIndex = this.permissionChanges.removed.indexOf(permissionName)
          if (removeIndex > -1) {
            this.permissionChanges.removed.splice(removeIndex, 1)
          }
        } else if (wasDirectPerm) {
          const removeIndex = this.permissionChanges.removed.indexOf(permissionName)
          if (removeIndex > -1) {
            this.permissionChanges.removed.splice(removeIndex, 1)
          }
        }
        
        if (!this.userPermissions.all_permissions.includes(permissionName)) {
          this.userPermissions.all_permissions.push(permissionName)
        }
        if (!isRolePerm && !this.userPermissions.direct_permissions.includes(permissionName)) {
          this.userPermissions.direct_permissions.push(permissionName)
        }
      } else {
        if (isRolePerm) {
          if (!this.permissionChanges.removed.includes(permissionName)) {
            this.permissionChanges.removed.push(permissionName)
          }
        } else if (wasDirectPerm) {
          if (!this.permissionChanges.removed.includes(permissionName)) {
            this.permissionChanges.removed.push(permissionName)
          }
          const addIndex = this.permissionChanges.added.indexOf(permissionName)
          if (addIndex > -1) {
            this.permissionChanges.added.splice(addIndex, 1)
          }
        } else {
          const addIndex = this.permissionChanges.added.indexOf(permissionName)
          if (addIndex > -1) {
            this.permissionChanges.added.splice(addIndex, 1)
          }
        }
        
        const allIndex = this.userPermissions.all_permissions.indexOf(permissionName)
        if (allIndex > -1) {
          this.userPermissions.all_permissions.splice(allIndex, 1)
        }
        const directIndex = this.userPermissions.direct_permissions.indexOf(permissionName)
        if (directIndex > -1) {
          this.userPermissions.direct_permissions.splice(directIndex, 1)
        }
      }
    },
    async savePermissions() {
      if (this.permissionChanges.added.length === 0 && this.permissionChanges.removed.length === 0) {
        return
      }
      
      try {
        await permissionService.updateUserPermissions({
          user_id: this.selectedUser.id,
          added: this.permissionChanges.added,
          removed: this.permissionChanges.removed
        })
        
        this.permissionsModal = false
        await this.getUsers()
      } catch (error) {
        console.error('Error saving permissions:', error)
      }
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.user.organization_id = this.profile.organization_id
    await this.getUsers()
    if(this.$route.query.add)
      this.userModal = true
  },

  watch: {
    userModal: function () {
      if (!this.userModal) {
        this.user = {
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          role: '',
          status: 'active',
        }
        this.userMode = 'add'
      }
    },
    permissionsModal: function () {
      if (!this.permissionsModal) {
        this.selectedUser = null
        this.allPermissions = {}
        this.userPermissions = {
          all_permissions: [],
          direct_permissions: [],
          role_permissions: []
        }
        this.permissionChanges = { added: [], removed: [] }
        this.expandedCategories = []
        this.originalDirectPermissions = []
        this.permissionSearch = ''
      }
    },
    permissionSearch: function (newVal) {
      if (newVal && newVal.trim()) {
        this.expandedCategories = Object.keys(this.filteredPermissions)
      } else {
        this.expandedCategories = []
      }
    },
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.updateRouteQuery()
      this.getUsers()
    },
    dataTablePage(){
      this.updateRouteQuery()
      this.getUsers()
    },
  },

}
</script>

<style scoped>
/* Users page styles */
.users-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.user-row {
  transition: all 0.2s ease;
}

.user-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
